import { keyframes } from "@emotion/react";
import React, { forwardRef } from "react";
import Box, { BoxProps } from "./box/Box";
import VisuallyHidden from "./VisuallyHidden";

export interface ISpinnerProps {
  /**
   * The size of the spinner
   */
  size?: "2xs" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
  /**
   * The color of the empty area in the spinner
   */
  emptyColor?: string;
  /**
   * The color of the spinner
   */
  color?: string;
  /**
   * The thickness of the spinner
   * @example
   * ```jsx
   * <LoadingSpinner thickness="4px"/>
   * ```
   */
  thickness?: string;
  /**
   * The speed of the spinner.
   * @example
   * ```jsx
   * <LoadingSpinner speed="0.2s"/>
   * ```
   */
  speed?: string;
  /**
   * For accessibility, it's important to add a fallback loading text.
   * This text will be visible to screen readers.
   */
  label?: string;
}

export type SpinnerProps = Omit<BoxProps, "size"> & ISpinnerProps;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const sizes = {
  "2xs": "0.65rem",
  xs: "1.3rem",
  sm: "1.8rem",
  md: "2.4rem",
  lg: "3rem",
  xl: "3.65rem",
  "2xl": "4.25rem",
};

const LoadingSpinner: React.FC<SpinnerProps> = forwardRef<any, SpinnerProps>(
  (
    { size = "md", label = "Loading...", speed = "0.65s", color = "blue.600", emptyColor = "gray.200", ...props },
    ref,
  ) => {
    const _size = sizes[size] || size;

    return (
      <Box
        ref={ref}
        {...getDefaultStyle({ size })}
        display="inline-block"
        borderColor="currentColor"
        borderLeftColor={emptyColor}
        borderBottomColor={emptyColor}
        borderStyle="solid"
        rounded="full"
        color={color}
        animation={`${spin} ${speed} linear infinite`}
        size={_size}
        {...props}
      >
        {label && <VisuallyHidden>{label}</VisuallyHidden>}
      </Box>
    );
  },
);

function getDefaultStyle({ size }: Pick<SpinnerProps, "size">) {
  if (size === "2xs") {
    return {
      borderWidth: "1px",
    };
  }

  if (size === "xs" || size === "sm") {
    return {
      borderWidth: "2px",
    };
  }

  if (size === "md") {
    return {
      borderWidth: "3px",
    };
  }

  if (size === "lg") {
    return {
      borderWidth: "4px",
    };
  }

  if (size === "xl") {
    return {
      borderWidth: "5px",
    };
  }

  return {
    borderWidth: "6px",
  };
}

LoadingSpinner.displayName = "LoadingSpinner";

export default LoadingSpinner;
